import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./FullCirclePaywall.module.scss"

/**
 * FullCirclePaywall (`/fullcircle`) — a self-contained ONBOARDING paywall for
 * Private Photo Vault, built around a single idea a brand-new user can act on in
 * three seconds: **close the circle.**
 *
 * On day one your first memories sit gathered in the middle of the screen. A
 * protective ring waits, open, around them. You trace it closed — drag the bead
 * once around — and the ring seals into one unbroken loop of protection: every
 * month of the year lit, the whole cluster held private and safe. The yearly plan
 * IS that closed circle — a whole year, gathered and kept in one continuous loop.
 * The monthly plan is a single 1/12 arc you have to re-draw every month.
 *
 * Divergence brief: claude-paywalls/_brief.md · concept doc: claude-paywalls/full-circle.md
 *
 * Chosen so every axis lands where the ONBOARDING lane (Aurora / Abyss / Vault
 * Door / Longview / Amber / Sightline / Firstlight / Handsel) is NOT saturated:
 *   - Layout:      a RADIAL completion-ring gauge encircling a hex-cluster of the
 *                  user's first photos, with month markers around the rim. No
 *                  centered glowing card, ocean canvas, steel door, scrub-tower,
 *                  resin gem, before/after seam, appliance panel or gift box.
 *   - Interaction: TRACE-THE-RING — a continuous, forward-only circular sweep
 *                  (drag the bead / tap the rim / arrow keys) that draws the loop
 *                  closed 0→360°. Not a crank, resin-raise, seam-drag, power-tap
 *                  or ribbon-pull; and radial, not the linear pin-track of the
 *                  immediately-prior OpenShutter.
 *   - Register:    calm, resolved SATISFACTION of completion — the soft click of
 *                  a ring closing — not cosmic awe, deep calm, tactile pride,
 *                  hopeful momentum, precious permanence, relief-wink or delight.
 *   - Art:        LIGHT, clean, premium — ivory ground, one luminous brand-blue
 *                  ring warming to amber at the head, real bundled memory photos
 *                  in the middle so it reads as a photo VAULT instantly.
 *   - Value:      COMPLETION / WHOLENESS via goal-gradient + commitment — "your
 *                  whole year, come full circle." An honest onboarding frame:
 *                  finish the loop you just started. Yearly is the complete circle.
 *
 * Honest & App Store compliant: every claim maps to what a private photo/video
 * vault with automatic backup actually does — private & encrypted storage,
 * automatic backup of every new photo and video, full-quality originals, one
 * place on every device. The ring is plainly a UI gauge, not a claim about the
 * viewer's own library. Nothing implies data is lost when a plan ends — the copy
 * says the opposite. No countdown, no fake scarcity; the savings figure is exact
 * yearly-vs-12×monthly arithmetic. Yearly is the default, featured plan with the
 * most prominent price; monthly is present but small and secondary.
 */

type PlanKey = "yearly" | "monthly"

// Seven real, warm, candid "first memories" already bundled in the repo
// (public/byheart/*.jpg — no network egress), arranged as a gathered hex cluster
// in the centre of the ring. Positions are unit offsets from the cluster centre.
const CLUSTER: Array<{ id: string; x: number; y: number; r: number }> = [
  { id: "m07", x: 0, y: 0, r: -3 }, // heart of the cluster
  { id: "m01", x: -0.86, y: -0.5, r: -7 },
  { id: "m11", x: 0.86, y: -0.5, r: 6 },
  { id: "m05", x: 0.86, y: 0.5, r: -5 },
  { id: "m13", x: -0.86, y: 0.5, r: 7 },
  { id: "m09", x: 0, y: -1, r: 4 },
  { id: "m17", x: 0, y: 1, r: -6 },
]

const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"]

const clamp = (v: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v))

// ── inline glyphs (no new asset deps) ────────────────────────────────────────
const Tick = ({ size = 11 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const FullCirclePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── ring state ─────────────────────────────────────────────────────────────
  // `p` (0..1) is how far the protective loop has been drawn, clockwise from the
  // top. The ring only ever moves forward — you draw it closed, you never un-draw
  // it. Once p reaches 1 the circle is sealed.
  const [p, setP] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [hinted, setHinted] = useState(false)

  const pRef = useRef(0)
  const draggingRef = useRef(false)
  const sealedRef = useRef(false)
  const ringRef = useRef<HTMLDivElement>(null)
  const raf = useRef<number | null>(null)

  useEffect(() => {
    pRef.current = p
  }, [p])

  const commit = useCallback((next: number) => {
    const clamped = clamp(next)
    pRef.current = clamped
    setP(clamped)
    if (clamped >= 0.999 && !sealedRef.current) {
      sealedRef.current = true
      native.logMixpanelEvent("html_fullcircle_sealed", {})
    }
  }, [])

  // pointer position → progress, measured clockwise from 12 o'clock. Forward-only,
  // with a max per-move step so the wrap point (top) can't jump 0↔1 ambiguously.
  const angleToProgress = useCallback((clientX: number, clientY: number) => {
    const el = ringRef.current
    if (!el) return null
    const box = el.getBoundingClientRect()
    const cx = box.left + box.width / 2
    const cy = box.top + box.height / 2
    const dx = clientX - cx
    const dy = clientY - cy
    // atan2 with 12 o'clock as 0, increasing clockwise
    let a = Math.atan2(dx, -dy) // radians, -π..π, 0 at top, + clockwise
    if (a < 0) a += Math.PI * 2
    return a / (Math.PI * 2)
  }, [])

  const dragTo = useCallback(
    (clientX: number, clientY: number) => {
      const target = angleToProgress(clientX, clientY)
      if (target == null) return
      const cur = pRef.current
      // forward-only: accept the target if it's ahead of where we are, within a
      // reasonable step. Near completion, snap the last sliver shut.
      let next = target
      if (next < cur) {
        // wrapped past the top — only allow it to mean "finished the loop"
        if (cur > 0.55) next = 1
        else return
      }
      if (next - cur > 0.34) return // ignore big teleports across the dial
      if (next > 0.965) next = 1
      commit(next)
    },
    [angleToProgress, commit],
  )

  const stopRaf = useCallback(() => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
  }, [])
  useEffect(() => () => stopRaf(), [stopRaf])

  const beginDrag = useCallback(
    (e: React.PointerEvent) => {
      if (pRef.current >= 1) return
      e.preventDefault()
      setHinted(true)
      draggingRef.current = true
      setDragging(true)
      ringRef.current?.setPointerCapture?.(e.pointerId)
      dragTo(e.clientX, e.clientY)
    },
    [dragTo],
  )

  const moveDrag = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      dragTo(e.clientX, e.clientY)
    },
    [dragTo],
  )

  const endDrag = useCallback(() => {
    draggingRef.current = false
    setDragging(false)
  }, [])

  const advanceBy = useCallback(
    (delta: number) => {
      setHinted(true)
      commit(pRef.current + delta)
    },
    [commit],
  )

  const reset = useCallback(() => {
    stopRaf()
    sealedRef.current = false
    pRef.current = 0
    setP(0)
    native.logMixpanelEvent("html_fullcircle_reset", {})
  }, [stopRaf])

  // teaching nudge: on load the loop draws a small arc by itself, eases back a
  // touch, and waits — "trace it closed."
  useEffect(() => {
    if (reduceMotion) {
      pRef.current = 1
      setP(1)
      sealedRef.current = true
      setHinted(true)
      return
    }
    let cancelled = false
    const start = window.setTimeout(() => {
      const t0 = performance.now()
      const dur = 1050
      const peak = 0.12
      const tick = (n: number) => {
        if (cancelled || draggingRef.current || pRef.current > peak + 0.02)
          return
        const k = clamp((n - t0) / dur)
        // ease out to peak, then settle back slightly
        const eased =
          k < 0.75
            ? (k / 0.75) * peak
            : peak - (peak * 0.28 * (k - 0.75)) / 0.25
        pRef.current = eased
        setP(eased)
        if (k < 1) requestAnimationFrame(tick)
        else setHinted(true)
      }
      requestAnimationFrame(tick)
    }, 560)
    return () => {
      cancelled = true
      window.clearTimeout(start)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── derived display ─────────────────────────────────────────────────────────
  const sealed = p >= 0.999
  const monthsLit = Math.min(12, Math.floor(p * 12 + 0.0001))
  const pct = Math.round(p * 100)

  // geometry
  const R = 82
  const C = 2 * Math.PI * R
  const beadAngle = -90 + p * 360
  const beadRad = (beadAngle * Math.PI) / 180
  const beadX = 100 + R * Math.cos(beadRad)
  const beadY = 100 + R * Math.sin(beadRad)

  const status = sealed
    ? "Full circle — your whole year, sealed"
    : dragging
      ? "Sealing your year…"
      : hinted
        ? "Trace the ring to close the circle"
        : "Your first memories, gathered"

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Close the circle — ${trialDays} days free`
        : "Close the circle for a year"
      : "Continue month to month"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `then ${yearlyPrice}/year · cancel anytime`
        : `${yearlyPrice}/year · auto-renews · cancel anytime`
      : `${monthlyPrice}/month · auto-renews · cancel anytime`

  return (
    <div className={styles.page}>
      <div className={styles.wash} aria-hidden />

      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandDot} aria-hidden>
            <span className={styles.brandDotRing} />
          </span>
          <span className={styles.brandName}>Private Photo Vault</span>
          <span className={styles.premiumTag}>Premium</span>
        </div>
        <h1 className={styles.title}>
          {sealed ? (
            <>
              Your whole year,&nbsp;<em>come full circle.</em>
            </>
          ) : (
            <>
              Close the circle on&nbsp;<em>your first year.</em>
            </>
          )}
        </h1>
        <p className={styles.sub}>
          Draw the ring closed and every memory inside it is gathered, private
          and safe. A year of Premium is that unbroken loop — one continuous
          circle of protection around everything you capture.
        </p>
      </header>

      {/* ── the ring — the mechanic ──────────────────────────────────────────── */}
      <section
        className={styles.stage}
        aria-label="Trace the ring to seal your year"
      >
        <div
          ref={ringRef}
          className={`${styles.ring} ${dragging ? styles.ringLive : ""} ${
            sealed ? styles.ringSealed : ""
          }`}
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label="Trace the protective ring closed"
          onPointerDown={beginDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight" || e.key === "ArrowUp") {
              e.preventDefault()
              advanceBy(1 / 12)
            } else if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              commit(1)
              setHinted(true)
            }
          }}
        >
          <svg className={styles.svg} viewBox="0 0 200 200" aria-hidden>
            <defs>
              <linearGradient id="fcTrail" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#2b5ff5" />
                <stop offset="62%" stopColor="#5b8bff" />
                <stop offset="100%" stopColor="#ffaa45" />
              </linearGradient>
              <filter id="fcGlow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="3.2" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* the open track */}
            <circle
              className={styles.track}
              cx={100}
              cy={100}
              r={R}
              fill="none"
            />

            {/* month rim ticks */}
            <g className={styles.ticks}>
              {MONTHS.map((_, i) => {
                const a = (-90 + (i / 12) * 360) * (Math.PI / 180)
                const inner = R - 15
                const outer = R - 9
                return (
                  <line
                    key={i}
                    className={`${styles.tick} ${
                      p >= i / 12 - 0.001 ? styles.tickLit : ""
                    }`}
                    x1={100 + inner * Math.cos(a)}
                    y1={100 + inner * Math.sin(a)}
                    x2={100 + outer * Math.cos(a)}
                    y2={100 + outer * Math.sin(a)}
                  />
                )
              })}
            </g>

            {/* the drawn loop */}
            <circle
              className={styles.trail}
              cx={100}
              cy={100}
              r={R}
              fill="none"
              stroke="url(#fcTrail)"
              strokeLinecap="round"
              strokeDasharray={`${p * C} ${C}`}
              transform="rotate(-90 100 100)"
              filter={dragging || sealed ? "url(#fcGlow)" : undefined}
            />

            {/* the bead you drag */}
            {!sealed && (
              <g
                className={styles.bead}
                transform={`translate(${beadX} ${beadY})`}
              >
                <circle className={styles.beadHalo} r={11} />
                <circle className={styles.beadCore} r={7} />
                <circle className={styles.beadDot} r={2.6} />
              </g>
            )}
          </svg>

          {/* the gathered first memories */}
          <div
            className={`${styles.cluster} ${sealed ? styles.clusterOn : ""}`}
          >
            {CLUSTER.map((c, i) => {
              const kept = p >= (i + 1) / (CLUSTER.length + 1.4)
              return (
                <span
                  key={c.id}
                  className={`${styles.tile} ${kept ? styles.tileKept : ""}`}
                  style={{
                    ["--tx" as string]: `${c.x}`,
                    ["--ty" as string]: `${c.y}`,
                    ["--rot" as string]: `${c.r}deg`,
                    ["--i" as string]: `${i}`,
                    zIndex: c.x === 0 && c.y === 0 ? 7 : 3,
                  }}
                >
                  <img
                    src={`/byheart/${c.id}.jpg`}
                    alt=""
                    draggable={false}
                    loading="eager"
                  />
                  <span className={styles.tileTick} aria-hidden>
                    <Tick size={9} />
                  </span>
                </span>
              )
            })}
            <span className={styles.clusterHalo} aria-hidden />
          </div>

          {/* centre readout under the cluster */}
          <div className={styles.readout} aria-hidden>
            <span className={styles.readoutPct}>
              {sealed ? "Sealed" : `${pct}%`}
            </span>
            <span className={styles.readoutMonths}>
              {sealed ? "all 12 months" : `${monthsLit} of 12 months`}
            </span>
          </div>
        </div>

        <div className={styles.statusRow}>
          <span
            className={`${styles.statusDot} ${
              dragging
                ? styles.statusDotLive
                : sealed
                  ? styles.statusDotDone
                  : ""
            }`}
            aria-hidden
          />
          <span className={styles.status} aria-live="polite">
            {status}
          </span>
          {sealed && (
            <button className={styles.replay} onClick={reset}>
              Redraw
            </button>
          )}
        </div>
      </section>

      {/* honest feature notes — captions, not a 4-icon perk grid */}
      <ul className={styles.notes}>
        {[
          "Every photo and video, private & encrypted — locked to only you",
          "Automatic backup gathers each new memory the moment you capture it",
          "Full-quality originals, in one place, on every device you own",
        ].map((t) => (
          <li key={t}>
            <span className={styles.notesTick} aria-hidden>
              <Tick size={9} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── pricing · yearly = the full circle, featured ─────────────────────── */}
      <div className={styles.plans}>
        <span className={styles.plansLabel}>
          Choose how far the circle goes
        </span>

        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planGlyph} aria-hidden>
            <svg viewBox="0 0 40 40" width={34} height={34}>
              <circle
                cx={20}
                cy={20}
                r={14}
                fill="none"
                stroke="url(#fcTrail)"
                strokeWidth={4}
              />
            </svg>
          </span>
          <span className={styles.planMid}>
            <span className={styles.planName}>The full circle</span>
            <span className={styles.planDesc}>
              A whole year — one unbroken loop of protection
            </span>
            <span className={styles.planMeta}>
              {hasTrial ? `${trialDays}-day free trial · ` : ""}≈{" "}
              {yearlyPerMonth}
              /mo, billed yearly
            </span>
          </span>
          <span className={styles.planRight}>
            {savingsPct > 0 && (
              <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
            )}
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planArc} aria-hidden>
            <svg viewBox="0 0 40 40" width={26} height={26}>
              <path
                d="M20 7 A13 13 0 0 1 31 14"
                fill="none"
                stroke="#9aa4b8"
                strokeWidth={4}
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className={styles.planMonthMid}>
            <span className={styles.planMonthName}>A single month</span>
            <span className={styles.planMonthDesc}>
              One arc — re-draw it every month
            </span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaGlow} aria-hidden />
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours and always in your vault. Premium simply
        keeps every new memory gathered and protected, automatically — all year
        round.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default FullCirclePaywall
